/**
 * ImageMotion package catalogue + one-time double-credit offer.
 * Keep in sync with `public.imagemotion_package_config()` in
 * supabase/migrations/20261013100000_imagemotion_package_credits_6_12_22_32.sql
 * (imagemotion.test.ts asserts the two match).
 */

import { formatMoney, type RehubCurrency } from './currency';
import { PROPOSED_EUR_IMAGEMOTION_DOUBLE } from './proposed-eur';
import {
  IMAGEMOTION_PRO_CREDIT_COST,
  IMAGEMOTION_QUICK_CREDIT_COST,
  IMAGEMOTION_STANDARD_CREDIT_COST,
  IMAGEMOTION_ULTRA_CREDIT_COST,
} from './credit-actions';

export type ImagemotionPackageId =
  | 'tester'
  | 'quick'
  | 'standard'
  | 'premium'
  | 'ultra_premium'; // legacy only

export interface ImagemotionPackageDefinition {
  id: ImagemotionPackageId;
  label: string;
  credits: number;
  maxImages: number;
  description: string;
  badge: string | null;
  features: readonly string[];
  /** False for legacy packages that must not appear in the create flow. */
  selectable: boolean;
  /** Welcome / first-time package shown separately from paid cards. */
  isTester?: boolean;
}

export const IMAGEMOTION_PACKAGES: Record<ImagemotionPackageId, ImagemotionPackageDefinition> = {
  tester: {
    id: 'tester',
    label: 'Tester Video',
    credits: 6,
    maxImages: 3,
    description: 'Experience ImageMotion with your welcome credits.',
    badge: null,
    features: [
      'Animate up to 3 property photos',
      'Preview the same AI motion quality as paid videos',
      '1080p video with a small watermark',
    ],
    selectable: true,
    isTester: true,
  },
  quick: {
    id: 'quick',
    label: 'Quick Video',
    credits: IMAGEMOTION_QUICK_CREDIT_COST,
    maxImages: 10,
    description: 'Perfect for smaller properties and quick social media listings.',
    badge: null,
    features: [
      'Animate up to 10 property photos',
      'Smooth cinematic transitions',
      'Simple title or logo',
      'Professional 1080p video',
    ],
    selectable: true,
  },
  standard: {
    id: 'standard',
    label: 'Standard Video',
    credits: IMAGEMOTION_STANDARD_CREDIT_COST,
    maxImages: 15,
    description: 'Create professional property marketing videos with music, titles and branding.',
    badge: 'Most Popular',
    features: [
      'Animate up to 15 property photos',
      'Background music',
      'Custom titles and branding',
      'Intro and outro cards',
      'Professional 1080p video',
    ],
    selectable: true,
  },
  premium: {
    id: 'premium',
    label: 'Premium Video',
    credits: IMAGEMOTION_PRO_CREDIT_COST,
    maxImages: 25,
    description: 'Create a complete property showcase with advanced branding and more images.',
    badge: null,
    features: [
      'Animate up to 25 property photos',
      'Full branding controls',
      'Background music and titles',
      'Professional 1080p video',
    ],
    selectable: true,
  },
  ultra_premium: {
    id: 'ultra_premium',
    label: 'Ultra Premium',
    credits: IMAGEMOTION_ULTRA_CREDIT_COST,
    maxImages: 30,
    description: 'Legacy package — kept for historical projects',
    badge: null,
    features: ['Legacy package'],
    selectable: false,
  },
};

/** Paid packages shown in the create flow (excludes tester + legacy). */
export const IMAGEMOTION_PAID_PACKAGE_IDS: ImagemotionPackageId[] = ['quick', 'standard', 'premium'];

export function resolveImagemotionPackage(
  quality: string | null | undefined
): ImagemotionPackageDefinition {
  const key = (quality || 'standard') as ImagemotionPackageId;
  return IMAGEMOTION_PACKAGES[key] ?? IMAGEMOTION_PACKAGES.standard;
}

/** Recommend a package from image count + tester eligibility. */
export function recommendImagemotionPackage(
  imageCount: number,
  testerEligible: boolean
): ImagemotionPackageId {
  if (testerEligible && imageCount <= IMAGEMOTION_PACKAGES.tester.maxImages) {
    return 'tester';
  }
  if (imageCount <= IMAGEMOTION_PACKAGES.quick.maxImages) return 'quick';
  if (imageCount <= IMAGEMOTION_PACKAGES.standard.maxImages) return 'standard';
  return 'premium';
}

/** Standard+ feature gate (music, overlays, intro/outro). */
export function imagemotionHasStandardFeatures(quality: string): boolean {
  return quality === 'standard' || quality === 'premium' || quality === 'ultra_premium';
}

/** Premium+ feature gate (filters and advanced effects). */
export function imagemotionHasPremiumFeatures(quality: string): boolean {
  return quality === 'premium' || quality === 'ultra_premium';
}

export function imagemotionIsTester(quality: string): boolean {
  return quality === 'tester';
}

/** Lowest paid package — the tester is never the advertised "from" price. */
export const IMAGEMOTION_FROM_CREDITS = Math.min(
  ...IMAGEMOTION_PAID_PACKAGE_IDS.map((id) => IMAGEMOTION_PACKAGES[id].credits)
);

export const IMAGEMOTION_FROM_PRICE_COPY = `Create AI property videos from just ${IMAGEMOTION_FROM_CREDITS} credits.`;

/** One explanation under the package cards. Clip length is not repeated on each card. */
export const IMAGEMOTION_CLIP_EXPLAINER =
  'Every photo becomes a five-second animated clip, combined into one professional property video.';

export const IMAGEMOTION_TESTER_COMPLETION_COPY = {
  headline: 'Love your first video? Create a complete property showcase.',
  body: 'Upgrade to animate up to 25 photos, add your branding and create a professional marketing video.',
  primaryCta: 'Create a Full Video',
  secondaryCta: 'Explore Other ReHub Apps',
} as const;

export function imagemotionRemainingCreditsCopy(credits: number): string {
  const balance = Math.max(0, Math.floor(Number(credits) || 0));
  const noun = balance === 1 ? 'credit' : 'credits';
  return `You still have ${balance} ReHub ${noun} available to try our other AI tools.`;
}

// ---------------------------------------------------------------------------
// One-time double-credit offer
// ---------------------------------------------------------------------------

export type DoubleCreditOfferTier = 'starter_25_50' | 'best_50_100';

export interface DoubleCreditOfferDefinition {
  id: DoubleCreditOfferTier;
  name: string;
  purchasedCredits: number;
  bonusCredits: number;
  totalCredits: number;
  prices: { GBP: number; EUR: number; USD: number };
  bestValue?: boolean;
  /** Env var names for Stripe price IDs (never hardcode secrets). */
  priceEnvKeys: { GBP: string; EUR: string; USD: string };
  defaultStripePriceIds?: Partial<Record<RehubCurrency, string>>;
}

export const IMAGEMOTION_DOUBLE_CREDIT_OFFERS: Record<
  DoubleCreditOfferTier,
  DoubleCreditOfferDefinition
> = {
  starter_25_50: {
    id: 'starter_25_50',
    name: 'Starter offer',
    purchasedCredits: 25,
    bonusCredits: 25,
    totalCredits: 50,
    prices: { GBP: 22, EUR: PROPOSED_EUR_IMAGEMOTION_DOUBLE.double_25 / 100, USD: 29 },
    priceEnvKeys: {
      GBP: 'VITE_STRIPE_PRICE_IM_DOUBLE_25_GBP',
      EUR: 'VITE_STRIPE_PRICE_IM_DOUBLE_25_EUR',
      USD: 'VITE_STRIPE_PRICE_IM_DOUBLE_25_USD',
    },
    defaultStripePriceIds: {
      EUR: 'price_1UESm6DHlg75FZHHPgtgcNLC',
    },
  },
  best_50_100: {
    id: 'best_50_100',
    name: 'Best-value offer',
    purchasedCredits: 50,
    bonusCredits: 50,
    totalCredits: 100,
    prices: { GBP: 40, EUR: PROPOSED_EUR_IMAGEMOTION_DOUBLE.double_50 / 100, USD: 55 },
    bestValue: true,
    priceEnvKeys: {
      GBP: 'VITE_STRIPE_PRICE_IM_DOUBLE_50_GBP',
      EUR: 'VITE_STRIPE_PRICE_IM_DOUBLE_50_EUR',
      USD: 'VITE_STRIPE_PRICE_IM_DOUBLE_50_USD',
    },
    defaultStripePriceIds: {
      EUR: 'price_1UESm7DHlg75FZHHm0X2yE6q',
    },
  },
};

export const DOUBLE_CREDIT_OFFER_COPY = {
  headline: 'Double your first credit purchase',
  body:
    'Create more property videos with twice the credits. Choose 25 credits and receive 50, or choose 50 credits and receive 100. Available once per account. No subscription.',
  afterTesterHeadline: 'Ready to create the complete property video?',
  afterTesterBody:
    'Double your first credit purchase and create longer videos with more images, music, titles and branding.',
} as const;

export function doubleCreditCtaLabel(
  tier: DoubleCreditOfferTier,
  currency: 'GBP' | 'EUR' | 'USD'
): string {
  const offer = IMAGEMOTION_DOUBLE_CREDIT_OFFERS[tier];
  const amount = offer.prices[currency];
  return `Get ${offer.totalCredits} credits for ${formatMoney(amount, currency)}`;
}
